import { Box, Divider, Menu, MenuItem, Tooltip } from '@mui/material';
import {
    HomeOutlined,
    MenuOpenOutlined,
    NotificationsNoneOutlined,
    SearchOutlined,
    SettingsOutlined,
} from '@mui/icons-material';
import {
    MouseEvent,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { tokens } from '@synergy/theme-palo';

import { PaloOwnCrumbProvider } from './palo-detail-view';
import { paloLinkProps, paloLinkReset } from './palo-link';
import { paloShortcutLabel } from './palo-shortcuts';

const c = tokens.color;

export interface PaloNavSubItem {
    label: string;
    /** Full target incl. query, e.g. '/purchase-orders?filter[status]=STATUS_CREATED'. */
    to: string;
    count?: number;
    alert?: boolean;
}

export interface PaloNavSubGroup {
    label: string;
    items: PaloNavSubItem[];
}

export interface PaloSection {
    key: string;
    label: string;
    icon: ReactNode;
    /** Default target when the rail icon is clicked. */
    to: string;
    /** Heading shown at the top of the contextual nav panel. */
    navTitle: string;
    groups: PaloNavSubGroup[];
}

/**
 * An entry of the avatar menu. Signing out is a full page load to the login
 * host, so most of these carry a href rather than a handler.
 */
export interface PaloUserMenuItem {
    label: string;
    /** Navigated to in the same tab. Takes precedence over onClick. */
    href?: string;
    onClick?: () => void;
    icon?: ReactNode;
    /** A label over the entries below it, not an entry of its own. */
    heading?: boolean;
}

export interface PaloChromeProps {
    sections: PaloSection[];
    activeSectionKey?: string;
    /** Current location as pathname + search, used to mark the active sub-item. */
    activePath?: string;
    onNavigate?: (to: string) => void;
    /** Opens the command palette (topbar search / ⌘K). */
    onSearchClick?: () => void;
    /** Opens the settings; the gear at the foot of the rail. */
    onSettingsClick?: () => void;
    /** Breadcrumb in the topbar; defaults to the active section's title. */
    crumb?: ReactNode;
    /**
     * Quiet text after the breadcrumb — in the portals the company someone is
     * signed in for. It belongs in plain sight: a contact who looks after two
     * member companies otherwise cannot tell which one a page is about
     * (#2215, R-71).
     */
    crumbNote?: ReactNode;
    /**
     * Where the person is, when the same login also leads into another
     * interface — "Du bist im Mitgliederportal" beside the webshop (#2282).
     * Stays in the bar on every page, a record mask's included.
     */
    placeLabel?: ReactNode;
    /** Right-hand slot of the bar, before the bell: a way out, such as back to the webshop. */
    topbarActions?: ReactNode;
    /** Count on the notification bell; omit or 0 to hide the badge. */
    notificationCount?: number;
    onNotificationsClick?: () => void;
    userInitials?: string;
    /** Shown at the head of the avatar menu; without it the avatar is inert. */
    userName?: string;
    /** Second line under the name — the company someone is signed in for. */
    userSubline?: string;
    userMenuItems?: PaloUserMenuItem[];
    logo?: ReactNode;
}

/** A first and last name become two letters; a single name gives one. */
export const paloInitials = (name?: string): string | undefined => {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);

    if (0 === parts.length) {
        return undefined;
    }

    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

const norm = (value?: string): string => {
    if (!value) {
        return '';
    }
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const RailButton = ({
    section,
    active,
    onNavigate,
}: {
    section: PaloSection;
    active: boolean;
    onNavigate?: (to: string) => void;
}): ReactElement => (
    <Tooltip title={section.label} placement="right">
        <Box
            {...paloLinkProps(section.to, onNavigate)}
            aria-label={section.label}
            sx={{
                ...paloLinkReset,
                width: 38,
                height: 38,
                borderRadius: tokens.radius.default + 'px',
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
                color: active ? '#fff' : c.chromeInk,
                backgroundColor: active ? c.accent : 'transparent',
                '&:hover': { backgroundColor: active ? c.accent : 'rgba(255,255,255,.06)', color: '#fff' },
                '& svg': { fontSize: 19 },
            }}
        >
            {section.icon}
        </Box>
    </Tooltip>
);

const NavRow = ({
    item,
    active,
    onNavigate,
}: {
    item: PaloNavSubItem;
    active: boolean;
    onNavigate?: (to: string) => void;
}): ReactElement => (
    <Box
        {...paloLinkProps(item.to, onNavigate)}
        sx={{
            ...paloLinkReset,
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            padding: '6px 14px 6px 16px',
            fontSize: '12.5px',
            color: active ? '#fff' : '#AEB7C2',
            backgroundColor: active ? 'rgba(255,255,255,.06)' : 'transparent',
            borderLeft: `2px solid ${active ? c.accent : 'transparent'}`,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(255,255,255,.045)', color: '#E4E9EF' },
        }}
    >
        <Box component="span" sx={{ flex: 1 }}>
            {item.label}
        </Box>
        {/* A view holding nothing wears no badge: a row of zeros is noise to
            read past, and the empty list says it better than a "0" does. */}
        {!!item.count && (
            <Box
                component="span"
                sx={{
                    // Amber, not red: a marked sub-view says "look here", and
                    // the largest of them holds 82.980 orders — the normal state
                    // of a third of them. Red would cry wolf.
                    backgroundColor: item.alert ? c.warnSoft : 'rgba(255,255,255,.14)',
                    color: item.alert ? c.warn : '#D5DBE2',
                    fontSize: '10px',
                    fontWeight: 600,
                    borderRadius: '9px',
                    padding: '1px 6px',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {item.count}
            </Box>
        )}
    </Box>
);

const NAV_OPEN_KEY = 'palo.nav.open';

/**
 * Whether the contextual navigation was left open.
 *
 * Folding it away gives the mask 212 pixels, which on a 13-inch screen is the
 * difference between reading a position row and scrolling sideways for it
 * (#2207). Where the panel stands is a habit, so it is remembered per browser.
 */
const readNavOpen = (): boolean => {
    try {
        return 'closed' !== localStorage.getItem(NAV_OPEN_KEY);
    } catch {
        return true;
    }
};

/**
 * How much window the panel wants beside it.
 *
 * The top bar of a list carries a search field and five actions — 713 pixels
 * measured on the inquiry list — and cannot wrap, because it is 48 pixels
 * tall. With the panel open, a 947 pixel window left it 520 and cut off the
 * rest, "Neu: Anfrage" included (#2231 R-125). Below this the panel folds
 * itself away, and it comes back as soon as there is room again.
 */
const NAV_ROOM = 1100;

const roomForNav = (): boolean => window.innerWidth >= NAV_ROOM;

/**
 * PALO back-office app shell per the prototype: a narrow icon rail (56px) of
 * top-level sections, a contextual navigation panel (212px) for the active
 * section, and the main area with a top bar and content. Presentational — the
 * section model, active state and navigation are provided by the host.
 */
export const PaloChrome = ({
    sections,
    activeSectionKey,
    activePath,
    onNavigate,
    onSearchClick,
    onSettingsClick,
    crumb,
    crumbNote,
    placeLabel,
    topbarActions,
    notificationCount,
    onNotificationsClick,
    userInitials = 'PA',
    userName,
    userSubline,
    userMenuItems,
    logo,
    children,
}: PropsWithChildren<PaloChromeProps>): ReactElement => {
    const activeSection = sections.find(s => s.key === activeSectionKey) ?? sections[0];
    const current = norm(activePath);
    const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);
    const [navOpen, setNavOpen] = useState(() => readNavOpen() && roomForNav());
    // Counted, not switched: a list inside a record's shell registers too.
    const [crumbOwners, setCrumbOwners] = useState(0);
    const announceCrumb = useCallback((own: boolean) => setCrumbOwners(count => count + (own ? 1 : -1)), []);
    const maskHasCrumb = crumbOwners > 0;
    const [headSlotElement, setHeadSlotElement] = useState<HTMLElement | null>(null);
    const headSlot = useMemo(
        () => ({ announce: announceCrumb, element: headSlotElement }),
        [announceCrumb, headSlotElement]
    );

    // Only the crossing is acted on, never the width itself: inside a width the
    // panel stays where it was put, so folding it away by hand on a wide screen
    // holds and opening it by hand on a narrow one holds too.
    useEffect(() => {
        let cramped = !roomForNav();

        const onResize = (): void => {
            if (cramped === !roomForNav()) {
                return;
            }

            cramped = !cramped;
            setNavOpen(cramped ? false : readNavOpen());
        };

        window.addEventListener('resize', onResize);

        return () => window.removeEventListener('resize', onResize);
    }, []);

    const toggleNav = (): void =>
        setNavOpen(open => {
            try {
                localStorage.setItem(NAV_OPEN_KEY, open ? 'closed' : 'open');
            } catch {
                // Blocked storage: the panel simply forgets between visits.
            }

            return !open;
        });
    const hasUserMenu = !!userName || !!userMenuItems?.length;

    const runUserItem = (item: PaloUserMenuItem): void => {
        setUserAnchor(null);

        if (item.href) {
            window.location.assign(item.href);

            return;
        }

        item.onClick?.();
    };

    return (
        // The stage banner pushes the page down by its height; a frame of a
        // full 100vh then lost its bottom, the user button with it.
        <Box
            sx={{
                display: 'flex',
                height: 'calc(100vh - var(--stage-banner-height, 0px))',
                overflow: 'hidden',
                backgroundColor: c.canvas,
            }}
        >
            {/* Rail */}
            <Box
                sx={{
                    width: tokens.size.railW,
                    flexShrink: 0,
                    backgroundColor: c.chrome,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '10px 0 8px',
                    gap: '4px',
                }}
            >
                {sections.map(section => (
                    <RailButton
                        key={section.key}
                        section={section}
                        active={section.key === activeSection?.key}
                        onNavigate={onNavigate}
                    />
                ))}
                <Box
                    sx={{
                        marginTop: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px',
                    }}
                >
                    <Tooltip title={onSettingsClick ? 'Einstellungen' : ''} placement="right">
                        <SettingsOutlined
                            role={onSettingsClick ? 'button' : undefined}
                            aria-label={onSettingsClick ? 'Einstellungen' : undefined}
                            onClick={onSettingsClick}
                            sx={{
                                fontSize: 17,
                                color: c.chromeInk,
                                cursor: onSettingsClick ? 'pointer' : 'default',
                                '&:hover': onSettingsClick ? { color: c.ink } : undefined,
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={hasUserMenu ? userName ?? '' : ''} placement="right">
                        <Box
                            role={hasUserMenu ? 'button' : undefined}
                            aria-label={hasUserMenu ? userName : undefined}
                            onClick={
                                hasUserMenu
                                    ? (event: MouseEvent<HTMLElement>) => setUserAnchor(event.currentTarget)
                                    : undefined
                            }
                            sx={{
                                width: 27,
                                height: 27,
                                borderRadius: '50%',
                                backgroundColor: '#46505C',
                                color: '#fff',
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: '10.5px',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            {userInitials}
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            {hasUserMenu && (
                <Menu
                    open={!!userAnchor}
                    anchorEl={userAnchor}
                    onClose={() => setUserAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: 208,
                                marginLeft: '6px',
                                borderRadius: tokens.radius.default + 'px',
                                border: `1px solid ${c.line}`,
                                boxShadow: tokens.shadow.s2,
                            },
                        },
                    }}
                >
                    {userName && (
                        <Box sx={{ padding: '8px 14px 9px' }}>
                            <Box sx={{ fontSize: '12.5px', fontWeight: 620, color: c.ink }}>{userName}</Box>
                            {userSubline && (
                                <Box sx={{ fontSize: '11.5px', color: c.ink3, marginTop: '1px' }}>{userSubline}</Box>
                            )}
                        </Box>
                    )}
                    {userName && !!userMenuItems?.length && <Divider sx={{ borderColor: c.lineSoft }} />}
                    {userMenuItems?.map(item =>
                        item.heading ? (
                            <Box
                                key={item.label}
                                sx={{
                                    padding: '8px 14px 3px',
                                    fontSize: '10px',
                                    fontWeight: 650,
                                    letterSpacing: '.07em',
                                    textTransform: 'uppercase',
                                    color: c.ink3,
                                }}
                            >
                                {item.label}
                            </Box>
                        ) : (
                            <MenuItem
                                key={item.label}
                                onClick={() => runUserItem(item)}
                                sx={{
                                    fontSize: '12.5px',
                                    color: c.ink,
                                    gap: '9px',
                                    padding: '7px 14px',
                                    '& svg': { fontSize: 16, color: c.ink3 },
                                }}
                            >
                                {item.icon}
                                {item.label}
                            </MenuItem>
                        )
                    )}
                </Menu>
            )}

            {/* Contextual nav */}
            <Box
                sx={{
                    width: navOpen ? tokens.size.navW : 0,
                    flexShrink: 0,
                    backgroundColor: c.chrome2,
                    borderRight: navOpen ? `1px solid ${c.chromeLine}` : 'none',
                    padding: navOpen ? '12px 0' : 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    visibility: navOpen ? 'visible' : 'hidden',
                    transition: 'width .12s ease',
                }}
            >
                <Box
                    sx={{
                        padding: '2px 16px 26px',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#fff',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {logo ?? 'PALO'}
                </Box>
                {activeSection && (
                    <>
                        <Box
                            sx={{
                                padding: '4px 14px 10px',
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ fontSize: '13.5px', fontWeight: 640, color: '#EEF1F5' }}>
                                {activeSection.navTitle}
                            </Box>
                            <Box
                                sx={{
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    color: '#6C7786',
                                    textTransform: 'uppercase',
                                    letterSpacing: '.09em',
                                }}
                            >
                                Bereich
                            </Box>
                        </Box>
                        {/* NAV-04: it opens the command palette rather than
                            taking input, but it has to look like the field it
                            stands for — without the frame it read as one more
                            menu entry. Height and font stay those of a menu
                            row, so the column keeps its rhythm; sticky, so it
                            survives the scroll of a long area. */}
                        {onSearchClick && (
                            <Box
                                sx={{
                                    // The frame floats, the backdrop does not:
                                    // a sticky row has to be opaque, or the
                                    // menu scrolls through it.
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    backgroundColor: c.chrome2,
                                    padding: '0 12px 6px 14px',
                                }}
                            >
                                <Box
                                    role="button"
                                    aria-label={`Suchen (${paloShortcutLabel('k', true)})`}
                                    title={`Suchen oder Befehl ausführen (${paloShortcutLabel('k', true)})`}
                                    onClick={onSearchClick}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '9px',
                                        padding: '5px 9px',
                                        fontSize: '12.5px',
                                        color: '#AEB7C2',
                                        backgroundColor: 'rgba(255,255,255,.05)',
                                        border: '1px solid rgba(255,255,255,.13)',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,.08)',
                                            borderColor: 'rgba(255,255,255,.22)',
                                            color: '#E4E9EF',
                                        },
                                    }}
                                >
                                    <SearchOutlined sx={{ fontSize: 15 }} />
                                    <Box component="span" sx={{ flex: 1 }}>
                                        Suchen
                                    </Box>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: '9.5px',
                                            fontWeight: 600,
                                            color: c.chromeInk,
                                            border: '1px solid rgba(255,255,255,.14)',
                                            borderRadius: '4px',
                                            padding: '1px 5px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {paloShortcutLabel('k', true)}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        {activeSection.groups.map(group => (
                            <Box key={group.label}>
                                <Box
                                    sx={{
                                        padding: '11px 14px 4px',
                                        fontSize: '9.5px',
                                        fontWeight: 600,
                                        color: '#6C7786',
                                        letterSpacing: '.09em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {group.label}
                                </Box>
                                {group.items.map(item => (
                                    <NavRow
                                        key={item.to}
                                        item={item}
                                        active={current === norm(item.to)}
                                        onNavigate={onNavigate}
                                    />
                                ))}
                            </Box>
                        ))}
                    </>
                )}
            </Box>

            {/* Main */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box
                    sx={{
                        height: tokens.size.topH,
                        flexShrink: 0,
                        backgroundColor: c.surface,
                        borderBottom: `1px solid ${c.line}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '0 16px',
                    }}
                >
                    <Box
                        role="button"
                        aria-label={navOpen ? 'Navigation einklappen' : 'Navigation ausklappen'}
                        aria-expanded={navOpen}
                        title={navOpen ? 'Navigation einklappen' : 'Navigation ausklappen'}
                        onClick={toggleNav}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            color: c.ink3,
                            '&:hover': { color: c.ink },
                        }}
                    >
                        <MenuOpenOutlined
                            sx={{
                                fontSize: 18,
                                transform: navOpen ? 'none' : 'scaleX(-1)',
                                transition: 'transform .12s',
                            }}
                        />
                    </Box>
                    <Box
                        {...paloLinkProps('/', onNavigate)}
                        aria-label="Zum Cockpit"
                        title="Zum Cockpit"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            color: c.ink3,
                            '&:hover': { color: c.ink },
                        }}
                    >
                        <HomeOutlined sx={{ fontSize: 17 }} />
                    </Box>
                    {placeLabel && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                fontSize: '11px',
                                fontWeight: 600,
                                color: c.inkInvert,
                                backgroundColor: c.chrome,
                                borderRadius: '10px',
                                padding: '2px 9px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {placeLabel}
                        </Box>
                    )}
                    {!maskHasCrumb && (
                        <Box
                            sx={{
                                // Same size as the crumb a record mask writes
                                // here, so the bar reads the same whichever
                                // kind of page is open.
                                fontSize: '14px',
                                color: c.ink3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                        >
                            <Box component="b" sx={{ color: c.ink, fontWeight: 640 }}>
                                {crumb ?? activeSection.navTitle}
                            </Box>
                            {crumbNote && <Box component="span">{crumbNote}</Box>}
                        </Box>
                    )}
                    {/* The mask puts its head row here, so a record costs one
                        row instead of two (06.10.2026). Empty on a list, where
                        the chrome names the area itself. */}
                    <Box
                        ref={setHeadSlotElement}
                        sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}
                    />

                    {/* One way in, and it sits in the area navigation (NAV-04).
                        The magnifier returns to the topbar only while that column
                        is collapsed, so the way in never disappears entirely. */}
                    <Box
                        sx={{
                            marginLeft: 'auto',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                        }}
                    >
                        {topbarActions}
                        {!navOpen && onSearchClick && (
                            <Box
                                role="button"
                                aria-label={`Suchen (${paloShortcutLabel('k', true)})`}
                                title={`Suchen oder Befehl ausführen (${paloShortcutLabel('k', true)})`}
                                onClick={onSearchClick}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    color: c.ink2,
                                    '&:hover': { color: c.ink },
                                }}
                            >
                                <SearchOutlined sx={{ fontSize: 17 }} />
                            </Box>
                        )}
                        {/* Only drawn once there is something behind it: a bell that
                            offers a pointer and does nothing is worse than no bell. */}
                        {(onNotificationsClick || !!notificationCount) && (
                            <Box
                                role={onNotificationsClick ? 'button' : undefined}
                                onClick={onNotificationsClick}
                                sx={{ position: 'relative', display: 'flex' }}
                            >
                                <NotificationsNoneOutlined
                                    sx={{
                                        fontSize: 17,
                                        color: c.ink2,
                                        cursor: onNotificationsClick ? 'pointer' : 'default',
                                    }}
                                />
                                {!!notificationCount && (
                                    <Box
                                        component="span"
                                        sx={{
                                            position: 'absolute',
                                            top: -4,
                                            right: -4,
                                            backgroundColor: c.crit,
                                            color: '#fff',
                                            fontSize: '9px',
                                            fontWeight: 650,
                                            minWidth: 15,
                                            height: 15,
                                            borderRadius: '8px',
                                            display: 'grid',
                                            placeItems: 'center',
                                            padding: '0 3px',
                                            border: `1.5px solid ${c.surface}`,
                                        }}
                                    >
                                        {notificationCount}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <PaloOwnCrumbProvider value={headSlot}>{children}</PaloOwnCrumbProvider>
                </Box>
            </Box>
        </Box>
    );
};
